import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, FileText, Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  CRITICAL_BADGE_CLASS,
  CRITICAL_FLAG_LABELS,
  DETECTION_CRITERIA,
  DETECTION_LABELS,
  ITEM_STATUS_BADGE_CLASS,
  ITEM_STATUS_LABELS,
  RISK_BADGE_CLASS,
  RISK_LABELS,
  formatPeriodLabel,
} from "@/modules/tramites/materialidad/materialidadConstants";
import {
  fetchMaterialidadCfdiPreview,
  materialidadFileDownloadUrl,
} from "@/modules/tramites/materialidad/materialidadApi";
import { Input } from "@/components/ui/input";

function StatusBadge({ status }) {
  const label = ITEM_STATUS_LABELS[status] || status;
  const cls = ITEM_STATUS_BADGE_CLASS[status] || ITEM_STATUS_BADGE_CLASS.pending;
  return (
    <span className={`inline-flex rounded-sm border px-2 py-0.5 text-[10px] font-semibold ${cls}`}>
      {label}
    </span>
  );
}

function CfdiPreviewCard({ preview, loading, clientId }) {
  if (loading) {
    return (
      <div className="rounded-sm border border-slate-200 p-3 text-xs text-slate-500 flex items-center gap-2">
        <Loader2 className="h-3 w-3 animate-spin" />
        Cargando CFDI…
      </div>
    );
  }
  if (!preview) return null;
  const total = Number(preview.total || 0);
  return (
    <div className="rounded-sm border border-slate-200 p-3 space-y-2 text-xs">
      <p className="font-semibold text-slate-900 m-0">CFDI vinculado</p>
      <p className="font-mono text-[10px] break-all m-0 text-slate-600">{preview.uuid}</p>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-slate-700">
        <span>Fecha</span>
        <span>{preview.fecha || "—"}</span>
        <span>Total</span>
        <span className="font-medium">${total.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span>
        <span>Método</span>
        <span>{preview.metodo_pago || "—"}</span>
        <span>Estado</span>
        <span>{preview.estado || "—"}</span>
        <span>Emisor</span>
        <span>{preview.nombre_emisor || preview.rfc_emisor || "—"}</span>
        <span>Receptor</span>
        <span>{preview.nombre_receptor || preview.rfc_receptor || "—"}</span>
      </div>
      {preview.conceptos_resumen ? (
        <p className="text-[11px] text-slate-600 m-0 border-t border-slate-100 pt-2">
          {preview.conceptos_resumen}
        </p>
      ) : null}
      {(preview.conceptos || []).length > 0 ? (
        <ul className="text-[10px] text-slate-600 m-0 pl-4 list-disc space-y-1">
          {preview.conceptos.map((c, idx) => (
            <li key={`${c.clave_prod_serv}-${idx}`}>
              {c.clave_prod_serv ? `${c.clave_prod_serv} — ` : ""}
              {c.descripcion || "Concepto"}
            </li>
          ))}
        </ul>
      ) : null}
      <Link
        to={`/visores-sat?focusUuid=${encodeURIComponent(preview.uuid)}${clientId ? `&focusClient=${encodeURIComponent(clientId)}` : ""}`}
        className="inline-flex items-center gap-1 text-xs text-[#0F2D5C] underline font-medium"
      >
        Abrir XML en Visor SAT
        <ExternalLink className="h-3 w-3" />
      </Link>
    </div>
  );
}

/**
 * Drawer de expediente por ítem — detalle sin duplicar Visor ni tareas.
 */
export default function MaterialidadExpedienteDrawer({
  open,
  onOpenChange,
  item,
  clientName,
  saving,
  onSaveNotes,
  onSaveBusinessReason,
  onUpload,
  uploadInputRef,
}) {
  const notes = item?.notes || "";
  const criteria = item?.detection_criteria
    || (item?.detection_code ? DETECTION_CRITERIA[item.detection_code] : null);
  const [cfdiPreview, setCfdiPreview] = useState(null);
  const [cfdiLoading, setCfdiLoading] = useState(false);
  const criticalFlags = (item?.risk_flags || []).filter((f) => CRITICAL_FLAG_LABELS[f]);
  const isCritical = Boolean(item?.critical_finding) || criticalFlags.length > 0;

  useEffect(() => {
    if (!open || !item?.cfdi_uuid || !item?.client_id) {
      setCfdiPreview(null);
      return undefined;
    }
    let cancelled = false;
    setCfdiLoading(true);
    fetchMaterialidadCfdiPreview(item.client_id, item.cfdi_uuid)
      .then((data) => {
        if (!cancelled) setCfdiPreview(data);
      })
      .catch(() => {
        if (!cancelled) setCfdiPreview(null);
      })
      .finally(() => {
        if (!cancelled) setCfdiLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, item?.cfdi_uuid, item?.client_id]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="materialidad-expediente-drawer">
        <SheetHeader>
          <SheetTitle className="text-[#0F2D5C]">Expediente de materialidad</SheetTitle>
          <SheetDescription>
            {clientName || item?.client_id || "Empresa"} · {formatPeriodLabel(item?.period_key)}
          </SheetDescription>
        </SheetHeader>

        {!item ? (
          <p className="text-sm text-slate-600 mt-6">Seleccione un faltante para ver el detalle.</p>
        ) : (
          <div className="mt-6 space-y-5 text-sm">
            <div className="rounded-sm border border-slate-200 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-slate-900">{item.category_label || item.label}</span>
                <StatusBadge status={item.status} />
              </div>
              {item.counterparty_name || item.counterparty_rfc ? (
                <p className="text-xs text-slate-600 m-0">
                  Contraparte: {item.counterparty_name || "—"} {item.counterparty_rfc ? `(${item.counterparty_rfc})` : ""}
                  {item.efos_status && item.efos_status !== "—" ? (
                    <span className="ml-2 text-[10px] uppercase font-semibold">
                      EFOS: {item.efos_status}
                    </span>
                  ) : null}
                </p>
              ) : null}
              {item.detection_code ? (
                <p className="text-[11px] text-sky-800 m-0">
                  Detección: {DETECTION_LABELS[item.detection_code] || item.detection_code}
                </p>
              ) : null}
              {criteria ? (
                <p className="text-[11px] text-slate-700 m-0 bg-slate-50 border border-slate-100 rounded-sm px-2 py-1.5">
                  <span className="font-semibold">Criterio: </span>
                  {criteria}
                </p>
              ) : null}
              {item.risk_level ? (
                <p className="text-[11px] m-0">
                  Riesgo:{" "}
                  <span className={`inline-flex rounded-sm border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${RISK_BADGE_CLASS[item.risk_level] || ""}`}>
                    {RISK_LABELS[item.risk_level] || item.risk_level}
                  </span>
                </p>
              ) : null}
              {isCritical ? (
                <div
                  className={`rounded-sm border px-2 py-1.5 text-[11px] ${CRITICAL_BADGE_CLASS}`}
                  data-testid="materialidad-critical-banner"
                >
                  <p className="font-semibold m-0 uppercase tracking-wide text-[10px]">Riesgo crítico — revisión inmediata</p>
                  <ul className="m-0 mt-1 pl-4 list-disc">
                    {criticalFlags.map((flag) => (
                      <li key={flag}>{CRITICAL_FLAG_LABELS[flag]}</li>
                    ))}
                  </ul>
                  <p className="m-0 mt-1 text-[10px] opacity-80">
                    Indicador preventivo del despacho; no es un dictamen de materialidad fiscal.
                  </p>
                </div>
              ) : null}
              <p className="text-[11px] text-slate-500 m-0">Origen: {item.source_hint || "manual"}</p>
            </div>

            {item.cfdi_uuid ? (
              <CfdiPreviewCard preview={cfdiPreview} loading={cfdiLoading} clientId={item.client_id} />
            ) : item.source_hint === "visor_cfdi" ? (
              <p className="text-xs text-slate-600 m-0">
                Checklist del periodo: revise CFDIs considerados en contabilidad en{" "}
                <Link to="/visores-sat" className="text-[#0F2D5C] underline font-medium">
                  Visores SAT
                </Link>
                {" "}y valide confronta antes de cerrar el mes.
              </p>
            ) : null}

            {item.file_id ? (
              <div className="space-y-1">
                <Label className="text-xs">Archivo cargado</Label>
                <a
                  href={materialidadFileDownloadUrl(item.file_id)}
                  className="inline-flex items-center gap-2 text-[#0F2D5C] underline text-xs font-medium"
                  download
                >
                  <FileText className="h-4 w-4" />
                  Descargar evidencia
                </a>
              </div>
            ) : null}

            {item.despacho_task_id ? (
              <div className="space-y-1">
                <Label className="text-xs">Tarea vinculada</Label>
                <Link
                  to="/despacho/tareas"
                  className="inline-flex items-center gap-1 text-xs text-[#0F2D5C] underline font-medium"
                >
                  Ver tareas del despacho
                  <ExternalLink className="h-3 w-3" />
                </Link>
                <p className="text-[10px] text-slate-500 m-0 font-mono">{item.despacho_task_id}</p>
              </div>
            ) : null}

            <div className="space-y-2 rounded-sm border border-slate-200 p-3">
              <p className="text-xs font-semibold text-slate-900 m-0">Razón de negocios (breve)</p>
              <p className="text-[10px] text-slate-500 m-0">
                Proporcional al importe. No se requiere ensayo jurídico por cada gasto.
              </p>
              <div className="space-y-1">
                <Label htmlFor="materialidad-business-need" className="text-xs">Necesidad</Label>
                <Textarea
                  id="materialidad-business-need"
                  key={`need-${item.id}`}
                  defaultValue={item.business_need || ""}
                  className="rounded-sm min-h-[56px] text-sm"
                  placeholder="¿Qué necesidad tenía la empresa?"
                  onBlur={(e) => onSaveBusinessReason?.(item, { business_need: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="materialidad-business-benefit" className="text-xs">Beneficio esperado</Label>
                <Textarea
                  id="materialidad-business-benefit"
                  key={`benefit-${item.id}`}
                  defaultValue={item.business_benefit || ""}
                  className="rounded-sm min-h-[56px] text-sm"
                  placeholder="¿Qué beneficio económico o operativo esperaba?"
                  onBlur={(e) => onSaveBusinessReason?.(item, { business_benefit: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="materialidad-business-authorized" className="text-xs">Autorizó</Label>
                <Input
                  id="materialidad-business-authorized"
                  key={`auth-${item.id}`}
                  defaultValue={item.business_authorized_by || ""}
                  className="rounded-sm text-sm h-9"
                  placeholder="Nombre o puesto quien autorizó"
                  onBlur={(e) => onSaveBusinessReason?.(item, { business_authorized_by: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="materialidad-item-notes">Observaciones</Label>
              <Textarea
                id="materialidad-item-notes"
                defaultValue={notes}
                key={item.id}
                className="rounded-sm min-h-[80px] text-sm"
                onBlur={(e) => onSaveNotes?.(item, e.target.value)}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <input
                ref={uploadInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onUpload?.(item, file);
                  e.target.value = "";
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-sm"
                disabled={saving}
                onClick={() => uploadInputRef?.current?.click()}
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Subir evidencia
              </Button>
            </div>

            <p className="text-[11px] text-slate-500 m-0 border-t border-slate-100 pt-3">
              Actualizado: {item.updated_at ? new Date(item.updated_at).toLocaleString("es-MX") : "—"}
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
